<!-- ArchiveBillsModal.vue -->
<template>
  <div class="modal fade" id="archiveBillsModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header bg-warning text-dark">
          <h5 class="modal-title">
            <i class="mdi mdi-archive-outline me-2"></i>
            {{ modalTitle }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <!-- Mode Toggle -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="btn-group w-100" role="group">
                <button
                    type="button"
                    class="btn"
                    :class="mode === 'archive' ? 'btn-warning' : 'btn-outline-warning'"
                    @click="switchMode('archive')">
                  <i class="mdi mdi-archive-arrow-down me-1"></i>
                  Archive Bills
                </button>
                <button
                    type="button"
                    class="btn"
                    :class="mode === 'view' ? 'btn-info' : 'btn-outline-info'"
                    @click="switchMode('view')">
                  <i class="mdi mdi-archive-eye-outline me-1"></i>
                  View Archived
                </button>
                <button
                    type="button"
                    class="btn"
                    :class="mode === 'restore' ? 'btn-success' : 'btn-outline-success'"
                    @click="switchMode('restore')">
                  <i class="mdi mdi-backup-restore me-1"></i>
                  Restore Bills
                </button>
              </div>
            </div>
          </div>

          <!-- Archive Mode -->
          <div v-if="mode === 'archive'">
            <!-- Filters for Archive -->
            <div class="row mb-4">
              <div class="col-md-3">
                <label class="form-label">Date From:</label>
                <input v-model="archiveFilters.dateFrom" class="form-control" type="date" required>
              </div>
              <div class="col-md-3">
                <label class="form-label">Date To:</label>
                <input v-model="archiveFilters.dateTo" class="form-control" type="date" required>
              </div>
              <div class="col-md-3">
                <label class="form-label">Revenue Stream:</label>
                <select v-model="archiveFilters.selectedStream" class="form-select" required>
                  <option value="">Select Revenue Stream</option>
                  <option v-for="stream in streams" :key="stream.id" :value="stream.incomeTypePrefix">
                    {{ stream.name }}
                  </option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">Search:</label>
                <div class="position-relative">
                  <input
                      v-model="archiveFilters.search"
                      @input="handleArchiveSearch"
                      class="form-control"
                      type="text"
                      placeholder="Search bills...">
                  <i class="mdi mdi-magnify position-absolute top-50 end-0 translate-middle-y me-3 text-muted"></i>
                </div>
                <small class="text-muted">Search by bill no, customer, location, etc.</small>
              </div>
            </div>

            <!-- Search Button (only show if no search results loaded yet and no search term) -->
            <div class="row mb-4" v-if="billsToArchive.length === 0 && !showNoArchiveResults && !archiveFilters.search">
              <div class="col-12">
                <button
                    @click="searchBillsToArchive"
                    class="btn btn-primary"
                    :disabled="!canSearch || searchingBills">
                  <i v-if="!searchingBills" class="mdi mdi-magnify me-1"></i>
                  <div v-else class="spinner-border spinner-border-sm me-1" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  {{ searchingBills ? 'Searching...' : 'Load Bills to Archive' }}
                </button>
                <small class="text-muted ms-2">
                  Select date range and revenue stream to get started, or use search to find specific bills
                </small>
              </div>
            </div>

            <!-- Real-time search indicator -->
            <div class="row mb-2" v-if="billsToArchive.length > 0 && (searchingBills || archiveFilters.search)">
              <div class="col-12">
                <div class="d-flex align-items-center">
                  <div v-if="searchingBills" class="spinner-border spinner-border-sm me-2" role="status">
                    <span class="visually-hidden">Searching...</span>
                  </div>
                  <small class="text-muted">
                    <span v-if="searchingBills">Searching...</span>
                    <span v-else-if="archiveFilters.search">
                      Search results for: "<strong>{{ archiveFilters.search }}</strong>"
                    </span>
                    <span v-else>Showing all bills for selected criteria</span>
                  </small>
                  <button
                      v-if="archiveFilters.search"
                      @click="clearArchiveSearch"
                      class="btn btn-sm btn-outline-secondary ms-auto">
                    <i class="mdi mdi-close me-1"></i>Clear Search
                  </button>
                </div>
              </div>
            </div>

            <!-- Bills to Archive Results -->
            <div v-if="billsToArchive.length > 0" class="mt-4">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0">
                  <i class="mdi mdi-file-document-multiple-outline me-1"></i>
                  Bills Found: {{ billsToArchive.length }}
                </h6>
                <div>
                  <span class="text-muted me-3">Total Amount: {{ formatPrice(totalArchiveAmount) }}</span>
                  <button
                      @click="archiveSelectedBills"
                      class="btn btn-warning"
                      :disabled="selectedBillsToArchive.length === 0 || archivingBills">
                    <i v-if="!archivingBills" class="mdi mdi-archive-arrow-down me-1"></i>
                    <div v-else class="spinner-border spinner-border-sm me-1" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    {{ archivingBills ? 'Archiving...' : `Archive Selected (${selectedBillsToArchive.length})` }}
                  </button>
                </div>
              </div>

              <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
                <table class="table table-sm table-hover">
                  <thead class="table-dark sticky-top">
                  <tr>
                    <th>
                      <input
                          type="checkbox"
                          @change="toggleSelectAll"
                          :checked="selectedBillsToArchive.length === billsToArchive.length && billsToArchive.length > 0">
                    </th>
                    <th>Bill No</th>
                    <th>Customer</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Date</th>
                    <th>Location</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="bill in billsToArchive" :key="bill.id">
                    <td>
                      <input
                          type="checkbox"
                          :value="bill.id"
                          v-model="selectedBillsToArchive">
                    </td>
                    <td>{{ bill.billNo }}</td>
                    <td>{{ bill.customerName }}</td>
                    <td>{{ bill.description }}</td>
                    <td>{{ formatPrice(bill.detailAmount) }}</td>
                    <td>{{ formatDate(bill.dateCreated) }}</td>
                    <td>{{ bill.subCountyName }}, {{ bill.wardName }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- No Results -->
            <div v-if="showNoArchiveResults" class="text-center py-4">
              <i class="mdi mdi-file-search-outline display-4 text-muted"></i>
              <h5 class="mt-2">No Bills Found</h5>
              <p class="text-muted">No unpaid bills found for the selected criteria.</p>
            </div>
          </div>

          <!-- View Archived Mode -->
          <div v-if="mode === 'view'">
            <!-- Filters for Viewing Archived -->
            <div class="row mb-4">
              <div class="col-md-3">
                <label class="form-label">Date From:</label>
                <input v-model="viewFilters.dateFrom" @change="loadArchivedBills" class="form-control" type="date">
              </div>
              <div class="col-md-3">
                <label class="form-label">Date To:</label>
                <input v-model="viewFilters.dateTo" @change="loadArchivedBills" class="form-control" type="date">
              </div>
              <div class="col-md-3">
                <label class="form-label">Revenue Stream:</label>
                <select v-model="viewFilters.selectedStream" @change="loadArchivedBills" class="form-select">
                  <option value="">All Streams</option>
                  <option v-for="stream in streams" :key="stream.id" :value="stream.incomeTypePrefix">
                    {{ stream.name }}
                  </option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">Search:</label>
                <div class="position-relative">
                  <input
                      v-model="viewFilters.search"
                      @input="handleViewSearch"
                      class="form-control"
                      type="text"
                      placeholder="Search archived bills...">
                  <i class="mdi mdi-magnify position-absolute top-50 end-0 translate-middle-y me-3 text-muted"></i>
                </div>
                <small class="text-muted">Search by bill no, customer, location, etc.</small>
              </div>
            </div>

            <!-- Search Button (only show if no results loaded yet) -->
            <div class="row mb-4" v-if="archivedBills.length === 0 && !showNoArchivedResults">
              <div class="col-12">
                <button
                    @click="loadArchivedBills"
                    class="btn btn-info"
                    :disabled="loadingArchived">
                  <i v-if="!loadingArchived" class="mdi mdi-magnify me-1"></i>
                  <div v-else class="spinner-border spinner-border-sm me-1" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  {{ loadingArchived ? 'Loading...' : 'Load Archived Bills' }}
                </button>
                <small class="text-muted ms-2">Click to load archived bills</small>
              </div>
            </div>

            <!-- Real-time search indicator -->
            <div class="row mb-2" v-if="archivedBills.length > 0 && (loadingArchived || viewFilters.search)">
              <div class="col-12">
                <div class="d-flex align-items-center">
                  <div v-if="loadingArchived" class="spinner-border spinner-border-sm me-2" role="status">
                    <span class="visually-hidden">Searching...</span>
                  </div>
                  <small class="text-muted">
                    <span v-if="loadingArchived">Searching archived bills...</span>
                    <span v-else-if="viewFilters.search">
                      Search results for: "<strong>{{ viewFilters.search }}</strong>"
                    </span>
                    <span v-else>Showing all archived bills</span>
                  </small>
                  <button
                      v-if="viewFilters.search"
                      @click="clearViewSearch"
                      class="btn btn-sm btn-outline-secondary ms-auto">
                    <i class="mdi mdi-close me-1"></i>Clear Search
                  </button>
                </div>
              </div>
            </div>

            <!-- Archived Bills Results -->
            <div v-if="archivedBills.length > 0" class="mt-4">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0">
                  <i class="mdi mdi-archive-outline me-1"></i>
                  Archived Bills: {{ archivedBills.length }}
                </h6>
                <span class="text-muted">Total Amount: {{ formatPrice(totalArchivedAmount) }}</span>
              </div>

              <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
                <table class="table table-sm table-hover">
                  <thead class="table-info sticky-top">
                  <tr>
                    <th>Bill No</th>
                    <th>Customer</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Archived Date</th>
                    <th>Original Date</th>
                    <th>Location</th>
                    <th>Actions</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="bill in archivedBills" :key="bill.id">
                    <td>{{ bill.billNo }}</td>
                    <td>{{ bill.customerName }}</td>
                    <td>{{ bill.description }}</td>
                    <td>{{ formatPrice(bill.detailAmount) }}</td>
                    <td>{{ formatDate(bill.archivedDate) }}</td>
                    <td>{{ formatDate(bill.originalDate) }}</td>
                    <td>{{ bill.subCountyName }}, {{ bill.wardName }}</td>
                    <td>
                      <button
                          @click="viewArchivedBill(bill)"
                          class="btn btn-sm btn-outline-info me-1">
                        <i class="mdi mdi-eye"></i>
                      </button>
                      <button
                          v-if="getRight('RESTORE BILLS')"
                          @click="restoreSingleBill(bill)"
                          class="btn btn-sm btn-outline-success"
                          :disabled="restoringBill === bill.id">
                        <i v-if="restoringBill !== bill.id" class="mdi mdi-backup-restore"></i>
                        <div v-else class="spinner-border spinner-border-sm" role="status">
                          <span class="visually-hidden">Loading...</span>
                        </div>
                      </button>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- No Archived Results -->
            <div v-if="showNoArchivedResults" class="text-center py-4">
              <i class="mdi mdi-archive-remove-outline display-4 text-muted"></i>
              <h5 class="mt-2">No Archived Bills Found</h5>
              <p class="text-muted">No archived bills found for the selected criteria.</p>
            </div>
          </div>

          <!-- Restore Mode -->
          <div v-if="mode === 'restore'">
            <!-- Filters for Restore -->
            <div class="row mb-4">
              <div class="col-md-3">
                <label class="form-label">Date From:</label>
                <input v-model="restoreFilters.dateFrom" @change="loadBillsToRestore" class="form-control" type="date">
              </div>
              <div class="col-md-3">
                <label class="form-label">Date To:</label>
                <input v-model="restoreFilters.dateTo" @change="loadBillsToRestore" class="form-control" type="date">
              </div>
              <div class="col-md-3">
                <label class="form-label">Revenue Stream:</label>
                <select v-model="restoreFilters.selectedStream" @change="loadBillsToRestore" class="form-select">
                  <option value="">All Streams</option>
                  <option v-for="stream in streams" :key="stream.id" :value="stream.incomeTypePrefix">
                    {{ stream.name }}
                  </option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">Search:</label>
                <div class="position-relative">
                  <input
                      v-model="restoreFilters.search"
                      @input="handleRestoreSearch"
                      class="form-control"
                      type="text"
                      placeholder="Search bills to restore...">
                  <i class="mdi mdi-magnify position-absolute top-50 end-0 translate-middle-y me-3 text-muted"></i>
                </div>
                <small class="text-muted">Search by bill no, customer, location, etc.</small>
              </div>
            </div>

            <!-- Search Button (only show if no results loaded yet) -->
            <div class="row mb-4" v-if="billsToRestore.length === 0 && !showNoRestoreResults">
              <div class="col-12">
                <button
                    @click="loadBillsToRestore"
                    class="btn btn-success"
                    :disabled="loadingRestore">
                  <i v-if="!loadingRestore" class="mdi mdi-magnify me-1"></i>
                  <div v-else class="spinner-border spinner-border-sm me-1" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  {{ loadingRestore ? 'Loading...' : 'Load Bills to Restore' }}
                </button>
                <small class="text-muted ms-2">Click to load archived bills available for restore</small>
              </div>
            </div>

            <!-- Real-time search indicator -->
            <div class="row mb-2" v-if="billsToRestore.length > 0 && (loadingRestore || restoreFilters.search)">
              <div class="col-12">
                <div class="d-flex align-items-center">
                  <div v-if="loadingRestore" class="spinner-border spinner-border-sm me-2" role="status">
                    <span class="visually-hidden">Searching...</span>
                  </div>
                  <small class="text-muted">
                    <span v-if="loadingRestore">Searching bills to restore...</span>
                    <span v-else-if="restoreFilters.search">
                      Search results for: "<strong>{{ restoreFilters.search }}</strong>"
                    </span>
                    <span v-else>Showing all bills available for restore</span>
                  </small>
                  <button
                      v-if="restoreFilters.search"
                      @click="clearRestoreSearch"
                      class="btn btn-sm btn-outline-secondary ms-auto">
                    <i class="mdi mdi-close me-1"></i>Clear Search
                  </button>
                </div>
              </div>
            </div>

            <!-- Bills to Restore Results -->
            <div v-if="billsToRestore.length > 0" class="mt-4">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0">
                  <i class="mdi mdi-backup-restore me-1"></i>
                  Bills Available for Restore: {{ billsToRestore.length }}
                </h6>
                <div>
                  <span class="text-muted me-3">Total Amount: {{ formatPrice(totalRestoreAmount) }}</span>
                  <button
                      @click="restoreSelectedBills"
                      class="btn btn-success"
                      :disabled="selectedBillsToRestore.length === 0 || restoringBills">
                    <i v-if="!restoringBills" class="mdi mdi-backup-restore me-1"></i>
                    <div v-else class="spinner-border spinner-border-sm me-1" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    {{ restoringBills ? 'Restoring...' : `Restore Selected (${selectedBillsToRestore.length})` }}
                  </button>
                </div>
              </div>

              <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
                <table class="table table-sm table-hover">
                  <thead class="table-success sticky-top">
                  <tr>
                    <th>
                      <input
                          type="checkbox"
                          @change="toggleSelectAllRestore"
                          :checked="selectedBillsToRestore.length === billsToRestore.length && billsToRestore.length > 0">
                    </th>
                    <th>Bill No</th>
                    <th>Customer</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Archived Date</th>
                    <th>Original Date</th>
                    <th>Location</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="bill in billsToRestore" :key="bill.id">
                    <td>
                      <input
                          type="checkbox"
                          :value="bill.id"
                          v-model="selectedBillsToRestore">
                    </td>
                    <td>{{ bill.billNo }}</td>
                    <td>{{ bill.customerName }}</td>
                    <td>{{ bill.description }}</td>
                    <td>{{ formatPrice(bill.detailAmount) }}</td>
                    <td>{{ formatDate(bill.archivedDate) }}</td>
                    <td>{{ formatDate(bill.originalDate) }}</td>
                    <td>{{ bill.subCountyName }}, {{ bill.wardName }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- No Restore Results -->
            <div v-if="showNoRestoreResults" class="text-center py-4">
              <i class="mdi mdi-backup-restore display-4 text-muted"></i>
              <h5 class="mt-2">No Bills Available for Restore</h5>
              <p class="text-muted">No archived bills found for the selected criteria.</p>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>

          <!-- Archive Mode Footer Actions -->
          <div v-if="mode === 'archive' && billsToArchive.length > 0">
            <span class="text-muted me-3">{{ selectedBillsToArchive.length }} of {{ billsToArchive.length }} selected</span>
          </div>

          <!-- Restore Mode Footer Actions -->
          <div v-if="mode === 'restore' && billsToRestore.length > 0">
            <span class="text-muted me-3">{{ selectedBillsToRestore.length }} of {{ billsToRestore.length }} selected</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getRight, execute, biller } from "@/api";

export default {
  name: "ArchiveBillsModal",
  props: {
    streams: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      mode: 'archive', // 'archive', 'view', 'restore'

      // Archive mode data
      archiveFilters: {
        dateFrom: '',
        dateTo: '',
        selectedStream: '',
        search: ''
      },
      billsToArchive: [],
      selectedBillsToArchive: [],
      searchingBills: false,
      archivingBills: false,
      showNoArchiveResults: false,

      // View mode data
      viewFilters: {
        dateFrom: '',
        dateTo: '',
        selectedStream: '',
        search: ''
      },
      archivedBills: [],
      loadingArchived: false,
      showNoArchivedResults: false,
      restoringBill: null,

      // Restore mode data
      restoreFilters: {
        dateFrom: '',
        dateTo: '',
        selectedStream: '',
        search: ''
      },
      billsToRestore: [],
      selectedBillsToRestore: [],
      loadingRestore: false,
      restoringBills: false,
      showNoRestoreResults: false,

      // Search timeout for debouncing
      archiveSearchTimeout: null,
      viewSearchTimeout: null,
      restoreSearchTimeout: null,

      // Alert data
      alert: {
        success: false,
        failed: false,
        info: false
      },
      message: ''
    }
  },
  computed: {
    modalTitle() {
      switch(this.mode) {
        case 'archive': return 'Archive Unpaid Bills';
        case 'view': return 'View Archived Bills';
        case 'restore': return 'Restore Archived Bills';
        default: return 'Bill Archive Management';
      }
    },

    canSearch() {
      return this.archiveFilters.dateFrom &&
          this.archiveFilters.dateTo &&
          this.archiveFilters.selectedStream;
    },

    totalArchiveAmount() {
      return this.billsToArchive.reduce((sum, bill) => sum + parseFloat(bill.detailAmount || 0), 0);
    },

    totalArchivedAmount() {
      return this.archivedBills.reduce((sum, bill) => sum + parseFloat(bill.detailAmount || 0), 0);
    },

    totalRestoreAmount() {
      return this.billsToRestore.reduce((sum, bill) => sum + parseFloat(bill.detailAmount || 0), 0);
    }
  },
  methods: {
    getRight,

    switchMode(newMode) {
      this.mode = newMode;
      this.resetAllData();
    },

    handleArchiveSearch() {
      // Allow search even without basic criteria - search across ALL unpaid bills
      // Clear previous timeout
      if (this.archiveSearchTimeout) {
        clearTimeout(this.archiveSearchTimeout);
      }

      // Set new timeout for debounced search
      this.archiveSearchTimeout = setTimeout(() => {
        this.searchBillsToArchive();
      }, 500); // Wait 500ms after user stops typing
    },

    clearArchiveSearch() {
      this.archiveFilters.search = '';
      this.handleArchiveSearch(); // Trigger search without search term
    },

    handleViewSearch() {
      // Clear previous timeout
      if (this.viewSearchTimeout) {
        clearTimeout(this.viewSearchTimeout);
      }

      // Set new timeout for debounced search
      this.viewSearchTimeout = setTimeout(() => {
        this.loadArchivedBills();
      }, 500); // Wait 500ms after user stops typing
    },

    clearViewSearch() {
      this.viewFilters.search = '';
      this.handleViewSearch(); // Trigger search without search term
    },

    handleRestoreSearch() {
      // Clear previous timeout
      if (this.restoreSearchTimeout) {
        clearTimeout(this.restoreSearchTimeout);
      }

      // Set new timeout for debounced search
      this.restoreSearchTimeout = setTimeout(() => {
        this.loadBillsToRestore();
      }, 500); // Wait 500ms after user stops typing
    },

    clearRestoreSearch() {
      this.restoreFilters.search = '';
      this.handleRestoreSearch(); // Trigger search without search term
    },

    resetAllData() {
      // Reset all data when switching modes
      this.billsToArchive = [];
      this.selectedBillsToArchive = [];
      this.archivedBills = [];
      this.billsToRestore = [];
      this.selectedBillsToRestore = [];
      this.showNoArchiveResults = false;
      this.showNoArchivedResults = false;
      this.showNoRestoreResults = false;

      // Clear search fields and timeouts
      this.archiveFilters.search = '';
      this.viewFilters.search = '';
      this.restoreFilters.search = '';

      if (this.archiveSearchTimeout) {
        clearTimeout(this.archiveSearchTimeout);
        this.archiveSearchTimeout = null;
      }
      if (this.viewSearchTimeout) {
        clearTimeout(this.viewSearchTimeout);
        this.viewSearchTimeout = null;
      }
      if (this.restoreSearchTimeout) {
        clearTimeout(this.restoreSearchTimeout);
        this.restoreSearchTimeout = null;
      }
    },

    async searchBillsToArchive() {
      try {
        this.searchingBills = true;
        this.billsToArchive = [];
        this.selectedBillsToArchive = [];
        this.showNoArchiveResults = false;

        const data = new FormData();
        data.append("function", "getBillsToArchive");
        data.append("status", "UNPAID");

        // If search term is provided, use it (backend will ignore other filters)
        if (this.archiveFilters.search) {
          data.append("search", this.archiveFilters.search);
        } else {
          // Only use date/stream filters when NOT searching and when they are provided
          if (this.archiveFilters.dateFrom) {
            data.append("dateFrom", this.archiveFilters.dateFrom);
          }
          if (this.archiveFilters.dateTo) {
            data.append("dateTo", this.archiveFilters.dateTo);
          }
          if (this.archiveFilters.selectedStream) {
            data.append("incomeTypePrefix", this.archiveFilters.selectedStream);
          }
        }

        const res = await execute(data, biller);
        this.searchingBills = false;

        if (res.data.success && res.data.data.billDetails) {
          this.billsToArchive = res.data.data.billDetails;
          if (this.billsToArchive.length === 0) {
            this.showNoArchiveResults = true;
          }
        } else {
          this.showNoArchiveResults = true;
          this.message = res.data.message || "No bills found for archiving";
        }
      } catch (e) {
        this.searchingBills = false;
        this.showNoArchiveResults = true;
        this.message = e.message;
        this.alert.failed = true;
      }
    },

    toggleSelectAll() {
      if (this.selectedBillsToArchive.length === this.billsToArchive.length) {
        this.selectedBillsToArchive = [];
      } else {
        this.selectedBillsToArchive = this.billsToArchive.map(bill => bill.id);
      }
    },

    async archiveSelectedBills() {
      if (this.selectedBillsToArchive.length === 0) return;

      if (!confirm(`Are you sure you want to archive ${this.selectedBillsToArchive.length} bill(s)? This action will move them to the archive.`)) {
        return;
      }

      try {
        this.archivingBills = true;

        const data = new FormData();
        data.append("function", "archiveBills");
        data.append("billIds", JSON.stringify(this.selectedBillsToArchive));
        data.append("sessionNames", sessionStorage.getItem("sessionNames"));
        data.append("reason", `Bulk archive for ${this.archiveFilters.selectedStream} bills from ${this.archiveFilters.dateFrom} to ${this.archiveFilters.dateTo}`);

        const res = await execute(data, biller);
        this.archivingBills = false;

        if (res.data.success) {
          this.message = `Successfully archived ${this.selectedBillsToArchive.length} bill(s)`;
          this.alert.success = true;

          // Remove archived bills from the list
          this.billsToArchive = this.billsToArchive.filter(bill =>
              !this.selectedBillsToArchive.includes(bill.id)
          );
          this.selectedBillsToArchive = [];

          // Emit event to parent to refresh the main list
          this.$emit('bills-archived');

        } else {
          this.message = res.data.message || "Failed to archive bills";
          this.alert.failed = true;
        }
      } catch (e) {
        this.archivingBills = false;
        this.message = e.message;
        this.alert.failed = true;
      }
    },

    async loadArchivedBills() {
      try {
        this.loadingArchived = true;
        this.archivedBills = [];
        this.showNoArchivedResults = false;

        const data = new FormData();
        data.append("function", "getArchivedBills");

        if (this.viewFilters.dateFrom) {
          data.append("dateFrom", this.viewFilters.dateFrom);
        }
        if (this.viewFilters.dateTo) {
          data.append("dateTo", this.viewFilters.dateTo);
        }
        if (this.viewFilters.selectedStream) {
          data.append("incomeTypePrefix", this.viewFilters.selectedStream);
        }
        if (this.viewFilters.search) {
          data.append("search", this.viewFilters.search);
        }

        const res = await execute(data, biller);
        this.loadingArchived = false;

        if (res.data.success && res.data.data.archivedBills) {
          this.archivedBills = res.data.data.archivedBills;
          if (this.archivedBills.length === 0) {
            this.showNoArchivedResults = true;
          }
        } else {
          this.showNoArchivedResults = true;
          this.message = res.data.message || "No archived bills found";
        }
      } catch (e) {
        this.loadingArchived = false;
        this.showNoArchivedResults = true;
        this.message = e.message;
        this.alert.failed = true;
      }
    },

    async loadBillsToRestore() {
      try {
        this.loadingRestore = true;
        this.billsToRestore = [];
        this.selectedBillsToRestore = [];
        this.showNoRestoreResults = false;

        const data = new FormData();
        data.append("function", "getArchivedBills");

        if (this.restoreFilters.dateFrom) {
          data.append("dateFrom", this.restoreFilters.dateFrom);
        }
        if (this.restoreFilters.dateTo) {
          data.append("dateTo", this.restoreFilters.dateTo);
        }
        if (this.restoreFilters.selectedStream) {
          data.append("incomeTypePrefix", this.restoreFilters.selectedStream);
        }
        if (this.restoreFilters.search) {
          data.append("search", this.restoreFilters.search);
        }

        const res = await execute(data, biller);
        this.loadingRestore = false;

        if (res.data.success && res.data.data.archivedBills) {
          this.billsToRestore = res.data.data.archivedBills;
          if (this.billsToRestore.length === 0) {
            this.showNoRestoreResults = true;
          }
        } else {
          this.showNoRestoreResults = true;
          this.message = res.data.message || "No archived bills found";
        }
      } catch (e) {
        this.loadingRestore = false;
        this.showNoRestoreResults = true;
        this.message = e.message;
        this.alert.failed = true;
      }
    },

    toggleSelectAllRestore() {
      if (this.selectedBillsToRestore.length === this.billsToRestore.length) {
        this.selectedBillsToRestore = [];
      } else {
        this.selectedBillsToRestore = this.billsToRestore.map(bill => bill.id);
      }
    },

    async restoreSelectedBills() {
      if (this.selectedBillsToRestore.length === 0) return;

      if (!confirm(`Are you sure you want to restore ${this.selectedBillsToRestore.length} bill(s)? This action will move them back to active bills.`)) {
        return;
      }

      try {
        this.restoringBills = true;

        const data = new FormData();
        data.append("function", "restoreBills");
        data.append("billIds", JSON.stringify(this.selectedBillsToRestore));
        data.append("sessionNames", sessionStorage.getItem("sessionNames"));

        const res = await execute(data, biller);
        this.restoringBills = false;

        if (res.data.success) {
          this.message = `Successfully restored ${this.selectedBillsToRestore.length} bill(s)`;
          this.alert.success = true;

          // Remove restored bills from the list
          this.billsToRestore = this.billsToRestore.filter(bill =>
              !this.selectedBillsToRestore.includes(bill.id)
          );
          this.selectedBillsToRestore = [];

          // Emit event to parent to refresh the main list
          this.$emit('bills-restored');

        } else {
          this.message = res.data.message || "Failed to restore bills";
          this.alert.failed = true;
        }
      } catch (e) {
        this.restoringBills = false;
        this.message = e.message;
        this.alert.failed = true;
      }
    },

    async restoreSingleBill(bill) {
      if (!confirm(`Are you sure you want to restore bill ${bill.billNo}?`)) {
        return;
      }

      try {
        this.restoringBill = bill.id;

        const data = new FormData();
        data.append("function", "restoreBills");
        data.append("billIds", JSON.stringify([bill.id]));
        data.append("sessionNames", sessionStorage.getItem("sessionNames"));

        const res = await execute(data, biller);
        this.restoringBill = null;

        if (res.data.success) {
          this.message = `Successfully restored bill ${bill.billNo}`;
          this.alert.success = true;

          // Remove restored bill from the list
          this.archivedBills = this.archivedBills.filter(b => b.id !== bill.id);

          // Emit event to parent to refresh the main list
          this.$emit('bills-restored');

        } else {
          this.message = res.data.message || "Failed to restore bill";
          this.alert.failed = true;
        }
      } catch (e) {
        this.restoringBill = null;
        this.message = e.message;
        this.alert.failed = true;
      }
    },

    viewArchivedBill(bill) {
      // Emit event to parent to show bill details
      this.$emit('view-archived-bill', bill);
    },

    formatPrice(value) {
      if (!value && value !== 0) return 'KES 0.00';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value);
    },

    formatDate(dateString) {
      if (!dateString) return '';
      try {
        return new Date(dateString).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (e) {
        return dateString;
      }
    }
  },

  mounted() {
    // Set default date range to current month
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    this.archiveFilters.dateFrom = firstDay.toISOString().split('T')[0];
    this.archiveFilters.dateTo = lastDay.toISOString().split('T')[0];

    this.viewFilters.dateFrom = firstDay.toISOString().split('T')[0];
    this.viewFilters.dateTo = lastDay.toISOString().split('T')[0];

    this.restoreFilters.dateFrom = firstDay.toISOString().split('T')[0];
    this.restoreFilters.dateTo = lastDay.toISOString().split('T')[0];
  }
}
</script>

<style scoped>
.modal-xl {
  max-width: 1200px;
}

.table th {
  white-space: nowrap;
  vertical-align: middle;
  font-weight: 600;
}

.table td {
  vertical-align: middle;
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
}

.btn-group .btn {
  flex: 1;
}

.form-label {
  font-weight: 600;
  font-size: 0.875rem;
}

.spinner-border-sm {
  width: 0.875rem;
  height: 0.875rem;
}

.table-responsive {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

.display-4 {
  font-size: 3.5rem;
}

.alert {
  border-radius: 0.5rem;
}

.badge {
  font-size: 0.75rem;
}
</style>